import { useState, useEffect, useCallback } from "react";
import {
  fetchPublicContentsBySlug,
  fetchPublicContents,
  PUBLIC_CONTENT_PAGE_LIMIT,
  PUBLIC_CONTENT_PAGE,
} from "../services/api";

/**
 * Loads published CMS content for the first matching slug.
 *
 * Primary: GET /user/content/public?limit=5&page=1&slug=<candidate>
 * Fallback: list fetch + client match if slug filter returns empty (legacy data).
 *
 * @param {string[]} slugCandidates - e.g. ['privacy-policy', 'privacy_policy']
 */
export function usePublicContentBySlug(slugCandidates) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    content: null,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));

    const candidates = (slugCandidates || []).filter(Boolean).map(String);

    try {
      let match = null;
      let lastFailureMessage = null;

      for (const slug of candidates) {
        const json = await fetchPublicContentsBySlug(slug, {
          limit: PUBLIC_CONTENT_PAGE_LIMIT,
          page: PUBLIC_CONTENT_PAGE,
        });

        if (!json?.status) {
          lastFailureMessage = json?.message || "Request failed";
          continue;
        }

        const items = json?.data?.items;
        if (Array.isArray(items) && items.length > 0) {
          const item = items[0];
          match = {
            title: item.title || "",
            body: item.body || "",
            slug: item.slug ?? slug,
            type: item.type,
            updatedAt: item.updatedAt,
            createdAt: item.createdAt,
          };
          break;
        }
      }

      if (!match && candidates.length > 0) {
        const json = await fetchPublicContents({ limit: 80, page: 1 });
        if (!json?.status) {
          setState({
            loading: false,
            error: json?.message || lastFailureMessage || "Request failed",
            content: null,
          });
          return;
        }
        const items = json?.data?.items;
        if (Array.isArray(items)) {
          for (const slug of candidates) {
            const found = items.find((item) => item?.slug === slug);
            if (found) {
              match = {
                title: found.title || "",
                body: found.body || "",
                slug: found.slug ?? slug,
                type: found.type,
                updatedAt: found.updatedAt,
                createdAt: found.createdAt,
              };
              break;
            }
          }
        }
      }

      setState({
        loading: false,
        error: null,
        content: match,
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not load content. Please try again later.";
      setState({ loading: false, error: String(message), content: null });
    }
  }, [slugCandidates]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refetch: load };
}
